import React from "react";
import { commitMutation, createRefetchContainer, graphql, type RelayRefetchProp } from "react-relay";

import { AttachmentsBaseClass, type AttachmentsBaseProps, type AttachmentsBaseState, type FellowAttachment } from ".";
import { Map } from "immutable";
import { compact, forEach } from "lodash";
import type { DeclarativeMutationConfig } from "relay-runtime";
import uuidv4 from "uuid/v4";

import { Extension } from "~/lib/prose/extension";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import toast from "~/ui/toast";

import AttachmentDropzone from "./AttachmentDropzone";

import type { AttachmentsRelay_note$data as Note } from "~/__generated__/AttachmentsRelay_note.graphql";
import type { AttachmentsRelayAddMutation as AddMutationType } from "~/__generated__/AttachmentsRelayAddMutation.graphql";
import type { AttachmentsRelayConfirmMutation as ConfirmMutationType } from "~/__generated__/AttachmentsRelayConfirmMutation.graphql";
import type { AttachmentsRelayUnlinkMutation as UnlinkMutationType } from "~/__generated__/AttachmentsRelayUnlinkMutation.graphql";

const AddMutation = graphql`
	mutation AttachmentsRelayAddMutation($input: RequestAddAttachmentInput!) {
		requestAddAttachment(input: $input) {
			ok
			fields
			url

			attachment {
				id
			}

			errors {
				message
			}
		}
	}
`;

const ConfirmMutation = graphql`
	mutation AttachmentsRelayConfirmMutation($input: ConfirmAddAttachmentInput!) {
		confirmAddAttachment(input: $input) {
			edge {
				node {
					id
					filename
					updatedAt
					url
					size
					filetype
					creator {
						id
						image
						fullName
					}
					dimensions {
						width
						height
					}
				}
			}
		}
	}
`;

const UnlinkMutation = graphql`
	mutation AttachmentsRelayUnlinkMutation($input: UnlinkAttachmentInput!) {
		unlinkAttachment(input: $input) {
			ok
			unlinkedAttachmentId
		}
	}
`;

interface AttachmentsRelayProps extends AttachmentsBaseProps {
	relay: RelayRefetchProp;
	note: Note;
}
interface AttachmentsRelayState extends AttachmentsBaseState {
	uploadingFiles: Map<string, { attachmentId: string | null; file: File }>;
}

export class AttachmentsRelay extends AttachmentsBaseClass<AttachmentsRelayProps, AttachmentsRelayState> {
	state: AttachmentsRelayState = {
		previewCache: Map(),
		uploadingFiles: Map(), // {uuid: {attachmentId, file}}
	};
	dropzoneRef: React.RefObject<AttachmentDropzone> = React.createRef();

	_refresh() {
		const { relay, note } = this.props;

		relay.refetch({ id: note.id });
	}

	open = () => {
		this.dropzoneRef.current?.onClickUpload();
	};

	upload = (files: File[]) => {
		dispatchAnalyticsTrack("[Attachment] upload attachments", {
			numberOfFiles: files.length,
			fileTypes: files.map(f => f.type),
		});

		const promises = files.map(
			file =>
				new Promise(resolve =>
					this.uploadSingleFile(file)
						.then(() => resolve({ ok: true, file }))
						.catch(() => resolve({ ok: false, file })),
				) as PromiseLike<{ ok: boolean; file: File }>,
		);

		return Promise.all(promises).then(res => {
			const numFiles = files.length;
			const numSuccess = res.filter(({ ok }) => ok).length;
			if (!numSuccess) return;
			let prompt = toast.success;
			if (numFiles != numSuccess) {
				prompt = toast.warn;
			}
			prompt(`Successfully uploaded ${numSuccess} file${numSuccess !== 1 ? "s" : ""}`);
			// this.socket?.send({ type: "REFRESH" });
		});
	};

	uploadSingleFile = (file: File) => {
		const uuid = uuidv4();
		const configs: DeclarativeMutationConfig[] = [
			{
				type: "RANGE_ADD",
				parentID: "note",
				connectionInfo: [
					{
						key: "content_attachments",
						filters: {
							contentId: this.props.groupId,
						},
						rangeBehavior: "prepend",
					},
				],
				edgeName: "edge",
			},
		];

		const promise = new Promise<{ uuid: string } & AddMutationType["response"]>((resolve, reject) => {
			this.setState(state => ({ uploadingFiles: state.uploadingFiles.set(uuid, { attachmentId: null, file }) }));
			return commitMutation<AddMutationType>(environment, {
				mutation: AddMutation,
				variables: {
					input: {
						contentId: this.props.groupId,
						filename: file.name,
						contentType: file.type || null,
						contentLength: file.size || null,
					},
				},
				onCompleted: (response, errors) => {
					if (errors || response.requestAddAttachment.errors) {
						this.setState(state => ({ uploadingFiles: state.uploadingFiles.delete(uuid) }));

						if (errors?.length) {
							return reject(new Error(errors[0].message));
						}
						if (response.requestAddAttachment.errors?.length) {
							const error = response.requestAddAttachment.errors[0];
							return reject(new Error(error ? error.message : ""));
						}
					}

					return resolve({ ...response, uuid });
				},
			});
		});

		return promise
			.then(({ uuid, requestAddAttachment: { attachment, fields, url } }) => {
				if (!url) return;
				const attachmentId = attachment ? attachment.id : null;
				this.setState(state => ({
					uploadingFiles: state.uploadingFiles.update(uuid, value => ({ ...value, attachmentId })),
				}));

				const formData = new FormData();
				forEach(fields, (value, key) => formData.set(key, value));
				formData.append("file", file, file.name);
				return fetch(url, { method: "POST", body: formData }).then(res => {
					if (res.ok) {
						return Promise.resolve(uuid);
					}
					this.setState(state => ({ uploadingFiles: state.uploadingFiles.delete(uuid) }));
					return Promise.reject(new Error(res.statusText));
				});
			})
			.then(uuid => {
				if (!uuid) return;
				const { attachmentId } = this.state.uploadingFiles.get(uuid);
				if (!attachmentId) return;
				this.setState(state => ({ previewCache: state.previewCache.set(attachmentId, URL.createObjectURL(file)) }));

				return new Promise((resolve, reject) => {
					return commitMutation<ConfirmMutationType>(environment, {
						mutation: ConfirmMutation,
						configs,
						variables: {
							input: {
								attachmentId,
							},
						},
						onCompleted: (response, errors) => {
							if (errors) {
								this.setState(state => ({ uploadingFiles: state.uploadingFiles.delete(uuid) }));

								if (errors?.length) {
									return reject(new Error(errors[0].message));
								}
							}
							return resolve(uuid);
						},
					});
				});
			})
			.catch(error => {
				toast.error(`Unable to upload ${file.name} - ${error.message}`);
				this.setState(state => ({ uploadingFiles: state.uploadingFiles.delete(uuid) }));
				return Promise.reject(error);
			});
	};

	unlink = (attachmentId: string) => {
		commitMutation<UnlinkMutationType>(environment, {
			mutation: UnlinkMutation,
			configs: [
				{
					type: "RANGE_DELETE",
					deletedIDFieldName: "unlinkedAttachmentId",
					parentID: "note",
					connectionKeys: [
						{
							key: "content_attachments",
							filters: {
								contentId: this.props.groupId,
							},
						},
					],
					pathToConnection: ["note", "attachments"],
				},
			],
			variables: {
				input: {
					attachmentId,
					contentId: this.props.groupId,
				},
			},
			onCompleted: () => {
				this.socket?.send({ type: "REFRESH" });
			},
		});
	};

	removeUploadedAttachments() {
		const { note } = this.props;
		const attachments = compact(note.attachments) as FellowAttachment[];
		const uploadedAttachmentIds = attachments.map(att => att.id);
		const hasConfirmedUploads = this.state.uploadingFiles.some(
			uf => uf !== undefined && uf.attachmentId !== null && uploadedAttachmentIds.includes(uf.attachmentId),
		);

		if (hasConfirmedUploads) {
			// once we confirmed that an upload as been saved in the server as an attachment,
			// then and not before, we can remove it from the list of uploading files
			this.setState(state => {
				return {
					uploadingFiles: state.uploadingFiles
						.filterNot(
							uf => uf !== undefined && uf.attachmentId !== null && uploadedAttachmentIds.includes(uf.attachmentId),
						)
						.toMap(),
				};
			});
		}
	}

	componentDidUpdate() {
		this.removeUploadedAttachments();
	}

	render() {
		const { note } = this.props;
		const attachments = compact(note.attachments) as FellowAttachment[];

		return (
			<AttachmentDropzone
				readonly={false}
				ref={this.dropzoneRef}
				contentId={this.props.groupId}
				attachments={attachments}
				upload={this.upload}
				previewCache={this.state.previewCache}
				unlink={this.unlink}
				uploadingFiles={this.state.uploadingFiles
					.entrySeq()
					.toArray()
					.map(([uuid, value]) => ({ ...value, uuid }))}
			/>
		);
	}
}

export default createRefetchContainer(
	AttachmentsRelay,
	{
		note: graphql`
			fragment AttachmentsRelay_note on Note {
				id
				attachments {
					id
					filename
					updatedAt
					url
					size
					filetype
					creator {
						id
						image
						fullName
					}
					dimensions {
						width
						height
					}
				}
			}
		`,
	},
	graphql`
		# Refetch Query, reusing the fragment from above.
		query AttachmentsRelay_refetchQuery($id: ID!) {
			note: node(id: $id) {
				... on Note {
					...AttachmentsRelay_note
				}
			}
		}
	`,
);

export class AttachmentsPlugin extends Extension {
	attachmentsContainer?: HTMLElement;

	get name() {
		return "attachments-relay";
	}

	init() {
		if (!this.editorView?.dom.parentNode || !this.options.setAttachmentsRef) return;

		this.attachmentsContainer = document.createElement("div");
		this.attachmentsContainer.setAttribute("data-prosemirror-attachments-container", "true");

		this.editorView.dom.parentNode.appendChild(this.attachmentsContainer);
		this.options.setAttachmentsRef(this.attachmentsContainer);
	}

	destroy() {
		if (!this.editorView?.dom.parentNode || !this.options.setAttachmentsRef || !this.attachmentsContainer) return;

		this.attachmentsContainer.remove();
	}
}
